import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';

import { db, storage } from './firebase';

export const generateChatId = (users, providerId) => {
  const sortedUsers = users.sort();
  return `${sortedUsers.join('_')}_${providerId}`;
};

export const fetchChatsWhereUserIsMember = async (usersId, providerId) => {
  try {
    const chatId = generateChatId(usersId, providerId);
    const docRef = doc(db, 'chats', chatId);
    const chatSnap = await getDoc(docRef);

    if (chatSnap.exists()) {
      return { id: chatSnap.id, ...chatSnap.data() };
    }

    if (!chatSnap.exists()) {
      await setDoc(docRef, {
        chatId,
        members: usersId,
        createdAt: serverTimestamp(),
        lastMessage: null,
        providerId,
      });

      return { id: chatId, chatId, members: usersId, createdAt: serverTimestamp(), lastMessage: null };
    }
  } catch (error) {
    throw error;
  }
};

export const fetchChannelsWhereUserIsMember = async channelId => {
  try {
    const docRef = doc(db, 'channels', channelId);
    const chatSnap = await getDoc(docRef);

    return { id: chatSnap.id, ...chatSnap.data() };
  } catch (error) {
    throw error;
  }
};

export const listenToChatMessages = async (type, chatId, callback) => {
  const collectionRef = type === 'channel' ? `channels/${chatId}/messages` : `chats/${chatId}/messages`;

  try {
    const messagesRef = collection(db, collectionRef);
    const messagesQuery = query(messagesRef, orderBy('createdAt', 'asc'));

    const unsubscribe = onSnapshot(
      messagesQuery,
      snapshot => {
        const messages = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        callback(messages);
      },
      error => {
        return error;
      }
    );

    return unsubscribe;
  } catch (error) {
    return error;
  }
};

export const sendMessageInChannel = async (chatId, message, users) => {
  const notificationIds = users.map(user => user.id);
  try {
    const messagesRef = collection(db, `channels/${chatId}/messages`);
    const newMessage = await addDoc(messagesRef, message);
    await updateDoc(doc(db, `channels/${chatId}`), {
      lastMessage: {
        ...message,
        id: newMessage.id,
        notificationIds: notificationIds,
      },
    });
  } catch (error) {
    throw error;
  }
};

export const sendMessageInChat = async (chatId, message) => {
  try {
    const messagesRef = collection(db, `chats/${chatId}/messages`);
    const newMessage = await addDoc(messagesRef, message);
    await updateDoc(doc(db, `chats/${chatId}`), { lastMessage: { ...message, id: newMessage.id } });
    await setDoc(
      doc(db, `notifications/${message.receiveId}`),
      {
        unreadChats: arrayUnion({
          chatId: chatId,
          timestamp: Date.now(),
          messageId: newMessage.id,
          message: message,
        }),
      },
      { merge: true }
    );
  } catch (error) {
    throw error;
  }
};

export const removeNotification = async (userId, chatId) => {
  try {
    const ref = doc(db, `notifications/${+userId}`);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
      return;
    }

    const data = snap.data();
    const currentUnreadChats = data?.unreadChats || [];

    const updatedUnreadChats = currentUnreadChats.filter(chat => chat.chatId !== chatId);

    await updateDoc(ref, {
      unreadChats: updatedUnreadChats,
    });
  } catch (error) {
    throw error;
  }
};

export const listenToNotifications = (userId, callback) => {
  try {
    const notificationsRef = doc(db, `notifications/${userId}`);
    const unsubscribe = onSnapshot(notificationsRef, snapshot => {
      const notifications = snapshot.data();
      callback(notifications);
    });
    return unsubscribe;
  } catch (error) {
    throw error;
  }
};

export const deleteMessage = (chatId, messageId) => {
  try {
    const messagesRef = doc(db, `chats/${chatId}/messages/${messageId}`);
    return deleteDoc(messagesRef);
  } catch (error) {
    throw error;
  }
};

export const deleteMessageChannel = (chatId, messageId) => {
  try {
    const messagesRef = doc(db, `channels/${chatId}/messages/${messageId}`);
    return deleteDoc(messagesRef);
  } catch (error) {
    throw error;
  }
};

export const uploadChat = async (file, onProgress) => {
  const imageRef = ref(storage, `chat_images/${Date.now()}_${file.name}`);

  const uploadTask = uploadBytesResumable(imageRef, file);

  return new Promise((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      snapshot => {
        // Progress function
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        if (onProgress) {
          onProgress(Math.round(progress));
        }
      },
      error => {
        // Error function
        reject(error);
      },
      async () => {
        // Complete function
        try {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(url);
        } catch (error) {
          reject(error);
        }
      }
    );
  });
};

export const getFileNameFromUrl = url => {
  try {
    const decodedUrl = decodeURIComponent(url);
    const match = decodedUrl.match(/\/o\/(.+?)\?/);
    if (!match || match.length < 2) {
      return null;
    }

    const fullPath = match[1];
    const rawFileName = fullPath.split('/').pop();
    const nameWithoutPrefix = rawFileName.substring(rawFileName.indexOf('_') + 1);

    const parts = nameWithoutPrefix.split('.');
    if (parts.length > 2) {
      parts.pop();
      return parts.join('.');
    }

    return nameWithoutPrefix;
  } catch (error) {
    return null;
  }
};

export const downloadFromFirebase = async docUrl => {
  try {
    const response = await fetch(docUrl);
    const blob = await response.blob();

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `${getFileNameFromUrl(docUrl)}`;
    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    return error;
  }
};

export const listenToChats = (userId, providerId, callback, errorCallback) => {
  try {
    const chatsRef = collection(db, 'chats');
    const q = query(chatsRef, where('providerId', '==', +providerId), where('members', 'array-contains', +userId));

    const unsubscribe = onSnapshot(
      q,
      snapshot => {
        const allChats = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        callback(allChats);
      },
      error => {
        if (errorCallback) {
          errorCallback(error);
        }
      }
    );

    return unsubscribe;
  } catch (error) {
    if (errorCallback) {
      errorCallback(error);
    }
    throw error;
  }
};

export const listenToChannels = (providerId, callback, errorCallback) => {
  try {
    const chatsRef = collection(db, 'channels');
    const q = query(chatsRef, where('providerId', '==', +providerId));

    const unsubscribe = onSnapshot(
      q,
      snapshot => {
        const allChats = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        callback(allChats);
      },
      error => {
        if (errorCallback) {
          errorCallback(error);
        }
      }
    );

    return unsubscribe;
  } catch (error) {
    if (errorCallback) {
      errorCallback(error);
    }
    throw error;
  }
};

export const listenToAllChannels = (userId, callback, errorCallback) => {
  try {
    const channelsRef = collection(db, 'channels');

    const unsubscribe = onSnapshot(
      channelsRef,
      snapshot => {
        const allChannels = snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter(channel => {
            // Filtrar apenas canais onde o usuário tem notificações não lidas
            return channel?.lastMessage?.notificationIds?.includes(+userId);
          });
        callback(allChannels);
      },
      error => {
        if (errorCallback) {
          errorCallback(error);
        }
      }
    );

    return unsubscribe;
  } catch (error) {
    if (errorCallback) {
      errorCallback(error);
    }
    throw error;
  }
};

export const createNewChannel = async (name, creator, providerId) => {
  try {
    const newChannel = {
      name,
      providerId,
      lastMessage: null,
      creator,
    };
    const channel = await addDoc(collection(db, 'channels'), newChannel);
    return {
      ...newChannel,
      id: channel.id,
    };
  } catch (error) {
    throw error;
  }
};

export const removeChannel = async channelId => {
  try {
    await deleteDoc(doc(db, 'channels', channelId));
  } catch (error) {
    throw error;
  }
};

export const removeChat = async chatId => {
  try {
    await deleteDoc(doc(db, 'chats', chatId));
  } catch (error) {
    throw error;
  }
};

export const removeNotificationChannel = async (userId, chatId) => {
  try {
    const channelRef = doc(db, 'channels', chatId);
    const channelSnap = await getDoc(channelRef);

    if (channelSnap.exists()) {
      await updateDoc(channelRef, { 'lastMessage.notificationIds': arrayRemove(+userId) });
    }
  } catch (error) {
    throw error;
  }
};
